import React from "react";
import { Dimensions, StyleSheet } from "react-native";
import { HTMLContentModel, HTMLElementModel, MixedStyleDeclaration, RenderHTML } from "react-native-render-html";
import WebView from 'react-native-webview';

import cheerio from 'cheerio';
import { PlaceTextStyle } from "../components/PlaceText";
import { IframeRenderer, IframeRenderersProps } from "./FindifyHTMLRender/IframeRenderer";
import { FindifyFormHTMLRenderer } from "./FindifyHTMLRender/FindifyFormRenderer";
import { iframeModel } from "@native-html/iframe-plugin";

export type FindifyHTMLRenderArgs = {
    baseStyles: MixedStyleDeclaration,
    htmlCode: string;
    width?: number | undefined;
    classesStyles?: Record<string, MixedStyleDeclaration>;
    tagsStyles?: Record<string, MixedStyleDeclaration>;
}
const defaultWebViewProps = {}


const renderers = {
    iframe: IframeRenderer,
    findifyformnode: FindifyFormHTMLRenderer,
}

const customHTMLElementModels = {
    iframe: iframeModel,
    findifyformnode: HTMLElementModel.fromCustomModel({
        tagName: 'findifyformnode',
        contentModel: HTMLContentModel.block
    })
};

const renderersProps = {
    iframe: IframeRenderersProps,
    findifyformnode: {},
}

const MemoRenderHTML = React.memo(RenderHTML);

export const FindifyHTMLRender = (props: FindifyHTMLRenderArgs) => {
    const width = props.width ?? Dimensions.get('window').width - 20;
    const source = { html: props.htmlCode };

    // return <HTMLRender
    return <MemoRenderHTML
        baseStyle={props.baseStyles}
        renderers={renderers}
        WebView={WebView}
        customHTMLElementModels={customHTMLElementModels}
        defaultWebViewProps={defaultWebViewProps}
        renderersProps={renderersProps}
        defaultTextProps={{ style: { ...PlaceTextStyle.font, fontSize: 16 } }}
        classesStyles={{ ...PlaygroundThemeStyleString, ...props.classesStyles }}
        tagsStyles={props.tagsStyles}
        contentWidth={width}
        source={source} />;
}


export function extractAttributesFromHTML(htmlString: string) {
    const $ = cheerio.load(htmlString);
    // `HTMLIframe` only needs attributes of the iframe element; avoid scanning the whole DOM.
    const iframe = $('iframe').first();
    if (iframe.length > 0) return iframe.attr() ?? {};

    const firstEl = $('*').first();
    return firstEl.attr() ?? {};
}



const PlaygroundThemeStyleString = StyleSheet.create({
    PlaygroundEditorTheme__ltr: {
        textAlign: 'left',
    },
    PlaygroundEditorTheme__rtl: {
        textAlign: 'right',
    },
    PlaygroundEditorTheme__paragraph: {
        margin: 0,
        position: 'relative',
    },
    PlaygroundEditorTheme__quote: {
        margin: 0,
        position: 'relative',
        marginLeft: 20,
        marginBottom: 10,
        fontSize: 15,
        color: 'rgb(101, 103, 107)',
        borderLeftColor: 'rgb(206, 208, 212)',
        borderLeftWidth: 4,
        // borderLeftStyle: 'solid',
        paddingLeft: 16,
    },
    PlaygroundEditorTheme__h1: {
        fontSize: 2,
    },
    PlaygroundEditorTheme__h2: {
        fontSize: 1.5,
    },
    PlaygroundEditorTheme__h3: {
        fontSize: 1.17,
    },
    PlaygroundEditorTheme__h4: {
        fontSize: 1,
    },
    PlaygroundEditorTheme__h5: {
        fontSize: 0.83,
    },
    PlaygroundEditorTheme__h6: {
        fontSize: 0.67,
    },
    PlaygroundEditorTheme__indent: {
        // You can set the variable value here if needed
    },
    PlaygroundEditorTheme__textBold: {
        fontWeight: 'bold',
    },
    PlaygroundEditorTheme__textItalic: {
        fontStyle: 'italic',
    },
    PlaygroundEditorTheme__textUnderline: {
        textDecorationLine: 'underline',
    },
    PlaygroundEditorTheme__textStrikethrough: {
        textDecorationLine: 'line-through',
    },
    PlaygroundEditorTheme__textUnderlineStrikethrough: {
        textDecorationLine: 'underline line-through',
    },
    PlaygroundEditorTheme__textSubscript: {
        fontSize: 0.8,
        // verticalAlign: 'sub',
    },
    PlaygroundEditorTheme__textSuperscript: {
        fontSize: 0.8,
        // verticalAlign: 'super',
    },
    PlaygroundEditorTheme__textCode: {
        backgroundColor: 'rgb(240, 242, 245)',
        padding: 1,
        paddingHorizontal: 0.25,
        fontFamily: 'Menlo, Consolas, Monaco, monospace',
        // fontSize: '94%',
    },
    PlaygroundEditorTheme__hashtag: {
        backgroundColor: 'rgba(88, 144, 255, 0.15)',
        borderBottomColor: 'rgba(88, 144, 255, 0.3)',
    },
    PlaygroundEditorTheme__link: {
        textDecorationLine: 'none',
    },
    PlaygroundEditorTheme__linkHover: {
        textDecorationLine: 'underline',
        textDecorationColor: 'inherit',
        // cursor: 'pointer',
    },
    PlaygroundEditorTheme__code: {
        backgroundColor: 'rgb(240, 242, 245)',
        fontFamily: 'Menlo, Consolas, Monaco, monospace',
        // display: 'block',
        padding: 8,
        paddingTop: 8,
        paddingRight: 8,
        paddingLeft: 52,
        lineHeight: 1.53,
        fontSize: 13,
        margin: 0,
        marginTop: 8,
        marginBottom: 8,
        // overflow: 'auto',
        position: 'relative',
    },
    PlaygroundEditorTheme__codeBefore: {
        // content: 'attr(data-gutter)',
        position: 'absolute',
        backgroundColor: '#eee',
        left: 0,
        top: 0,
        borderRightWidth: 1,
        borderRightColor: '#ccc',
        padding: 8,
        color: '#777',
        // whiteSpace: 'pre-wrap',
        textAlign: 'right',
        minWidth: 25,
    },
    PlaygroundEditorTheme__table: {
        // borderCollapse: 'collapse',
        // borderSpacing: 0,
        maxWidth: '100%',
        // overflowY: 'scroll',
        // tableLayout: 'fixed',
        // width: 'calc(100% - 25)',
        margin: 30,
    },
    PlaygroundEditorTheme__tableSelected: {
        // outlineWidth: 2,
        // outlineColor: 'rgb(60, 132, 244)',
    },
    PlaygroundEditorTheme__tableCell: {
        borderWidth: 1,
        borderColor: '#bbb',
        minWidth: 75,
        // verticalAlign: 'top',
        textAlign: 'left',
        padding: 6,
        paddingTop: 8,
        paddingRight: 8,
        paddingBottom: 8,
        position: 'relative',
        // cursor: 'default',
        // outlineWidth: 'none',
    },
    PlaygroundEditorTheme__tableCellSortedIndicator: {
        // display: 'block',
        opacity: 0.5,
        position: 'absolute',
        bottom: 0,
        left: 0,
        width: '100%',
        height: 4,
        backgroundColor: '#999',
    },
    PlaygroundEditorTheme__tableCellResizer: {
        position: 'absolute',
        right: -4,
        height: '100%',
        width: 8,
        // cursor: 'ew-resize',
        zIndex: 10,
        top: 0,
    },
    PlaygroundEditorTheme__tableCellHeader: {
        backgroundColor: '#f2f3f5',
        textAlign: 'left',
    },
    PlaygroundEditorTheme__tableCellSelected: {
        backgroundColor: '#c9dbf0',
    },
    PlaygroundEditorTheme__tableCellPrimarySelected: {
        borderWidth: 2,
        borderColor: 'rgb(60, 132, 244)',
        // display: 'block',
        // height: 'calc(100% - 2)',
        position: 'absolute',
        // width: 'calc(100% - 2)',
        left: -1,
        top: -1,
        zIndex: 2,
    },
    PlaygroundEditorTheme__tableCellEditing: {
        // boxShadow: '0 0 5px rgba(0, 0, 0, 0.4)',
        borderRadius: 3,
    },
    PlaygroundEditorTheme__tableAddColumns: {
        position: 'absolute',
        top: 0,
        width: 20,
        backgroundColor: '#eee',
        height: '100%',
        right: 0,
        // animation: 'table-controls 0.2s ease',
        borderWidth: 0,
        // cursor: 'pointer',
    },
    PlaygroundEditorTheme__tableAddColumnsAfter: {
        // backgroundImage: 'url(../../public/images/icons/plus.svg)',
        // backgroundSize: 'contain',
        // backgroundPosition: 'center',
        // backgroundRepeat: 'no-repeat',
        // display: 'block',
        // content: ' ',
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        opacity: 0.4,
    },
    PlaygroundEditorTheme__tableAddColumnsHover: {
        backgroundColor: '#c9dbf0',
    },
    PlaygroundEditorTheme__tableAddRows: {
        position: 'absolute',
        bottom: -25,
        // width: 'calc(100% - 25)',
        backgroundColor: '#eee',
        height: 20,
        left: 0,
        // animation: 'table-controls 0.2s ease',
        borderWidth: 0,
        // cursor: 'pointer',
    },
    PlaygroundEditorTheme__tableAddRowsAfter: {
        // backgroundImage: 'url(../../public/images/icons/plus.svg)',
        // backgroundSize: 'contain',
        // backgroundPosition: 'center',
        // backgroundRepeat: 'no-repeat',
        // display: 'block',
        // content: ' ',
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        opacity: 0.4,
    },
    PlaygroundEditorTheme__tableAddRowsHover: {
        backgroundColor: '#c9dbf0',
    },
    // '@keyframes table-controls': {
    //     '0%': {
    //         opacity: 0,
    //     },
    //     '100%': {
    //         opacity: 1,
    //     },
    // },
    PlaygroundEditorTheme__tableCellResizeRuler: {
        // display: 'block',
        position: 'absolute',
        width: 1,
        backgroundColor: 'rgb(60, 132, 244)',
        height: '100%',
        top: 0,
    },
    PlaygroundEditorTheme__tableCellActionButtonContainer: {
        // display: 'block',
        right: 5,
        top: 6,
        position: 'absolute',
        zIndex: 4,
        width: 20,
        height: 20,
    },
    PlaygroundEditorTheme__tableCellActionButton: {
        backgroundColor: '#eee',
        // display: 'block',
        borderWidth: 0,
        borderRadius: 20,
        width: 20,
        height: 20,
        color: '#222',
        // cursor: 'pointer',
    },
    PlaygroundEditorTheme__tableCellActionButtonHover: {
        backgroundColor: '#ddd',
    },
    PlaygroundEditorTheme__characterLimit: {
        // display: 'inline',
        backgroundColor: '#ffbbbb',
    },
    PlaygroundEditorTheme__ol1: {
        padding: 0,
        margin: 0,
        // listStylePosition: 'inside',
    },
    PlaygroundEditorTheme__ol2: {
        padding: 0,
        margin: 0,
        listStyleType: 'upper-alpha',
        // listStylePosition: 'inside',
    },
    PlaygroundEditorTheme__ol3: {
        padding: 0,
        margin: 0,
        listStyleType: 'lower-alpha',
        // listStylePosition: 'inside',
    },
    PlaygroundEditorTheme__ol4: {
        padding: 0,
        margin: 0,
        listStyleType: 'upper-roman',
        // listStylePosition: 'inside',
    },
    PlaygroundEditorTheme__ol5: {
        padding: 0,
        margin: 0,
        listStyleType: 'lower-roman',
        // listStylePosition: 'inside',
    },
    PlaygroundEditorTheme__ul: {
        padding: 0,
        margin: 0,
        // listStylePosition: 'inside',
    },
    PlaygroundEditorTheme__listItem: {
        margin: 0,
        marginLeft: 32,
        marginRight: 32,
    },
    PlaygroundEditorTheme__listItemChecked: {
        position: 'relative',
        marginLeft: 8,
        marginRight: 8,
        paddingLeft: 24,
        paddingRight: 24,
        listStyleType: 'none',
        // outlineWidth: 'none',
        textDecorationLine: 'line-through',
    },
    PlaygroundEditorTheme__listItemUnchecked: {
        position: 'relative',
        marginLeft: 8,
        marginRight: 8,
        paddingLeft: 24,
        paddingRight: 24,
        listStyleType: 'none',
        // outlineWidth: 'none',
    },
    PlaygroundEditorTheme__listItemUncheckedBefore: {
        // content: '',
        width: 16,
        height: 16,
        top: 2,
        left: 0,
        // cursor: 'pointer',
        // display: 'block',
        // backgroundSize: 'cover',
        position: 'absolute',
        borderWidth: 1,
        borderColor: '#999',
        borderRadius: 2,
    },
    PlaygroundEditorTheme__listItemCheckedBefore: {
        // content: '',
        width: 16,
        height: 16,
        top: 2,
        left: 0,
        // cursor: 'pointer',
        // display: 'block',
        // backgroundSize: 'cover',
        position: 'absolute',
        borderWidth: 1,
        borderColor: 'rgb(61, 135, 245)',
        borderRadius: 2,
        backgroundColor: '#3d87f5',
        // backgroundRepeat: 'no-repeat',

    },
    "PlaygroundEditorTheme__listItemUncheckedBefore[dir = 'rtl']": {
        left: 'auto',
        right: 0,
    },
    "PlaygroundEditorTheme__listItemCheckedBefore[dir = 'rtl']": {
        left: 'auto',
        right: 0,
    },
    PlaygroundEditorTheme__listItemUncheckedFocusBefore: {
        // boxShadow: '0 0 0 2px #a6cdfe',
        borderRadius: 2,
    },
    PlaygroundEditorTheme__listItemCheckedFocusBefore: {
        // boxShadow: '0 0 0 2px #a6cdfe',
        borderRadius: 2,
    },
    PlaygroundEditorTheme__listItemCheckedAfter: {
        borderColor: '#fff',
        borderStyle: 'solid',
        position: 'absolute',
        // display: 'block',
        top: 6,
        width: 3,
        left: 7,
        right: 7,
        height: 6,
        transform: 'rotate(45deg)',
        // borderWidth: '0 2 2 0',
    },
    PlaygroundEditorTheme__nestedListItem: {
        // listStyleType: 'none',
    },
    PlaygroundEditorTheme__nestedListItemBefore: {
        display: 'none',
    },
    PlaygroundEditorTheme__nestedListItemAfter: {
        display: 'none',
    },
    PlaygroundEditorTheme__tokenComment: {
        color: 'slategray',
    },
    PlaygroundEditorTheme__tokenPunctuation: {
        color: '#999',
    },
    PlaygroundEditorTheme__tokenProperty: {
        color: '#905',
    },
    PlaygroundEditorTheme__tokenSelector: {
        color: '#690',
    },
    PlaygroundEditorTheme__tokenOperator: {
        color: '#9a6e3a',
    },
    PlaygroundEditorTheme__tokenAttr: {
        color: '#07a',
    },
    PlaygroundEditorTheme__tokenVariable: {
        color: '#e90',
    },
    PlaygroundEditorTheme__tokenFunction: {
        color: '#dd4a68',
    },
    PlaygroundEditorTheme__mark: {
        backgroundColor: 'rgba(255, 212, 0, 0.14)',
        borderBottomWidth: 2,
        borderBottomColor: 'rgba(255, 212, 0, 0.3)',
        paddingBottom: 2,
    },
    PlaygroundEditorTheme__markOverlap: {
        backgroundColor: 'rgba(255, 212, 0, 0.3)',
        borderBottomWidth: 2,
        borderBottomColor: 'rgba(255, 212, 0, 0.7)',
    },
    PlaygroundEditorTheme__markSelected: {
        backgroundColor: 'rgba(255, 212, 0, 0.5)',
        borderBottomWidth: 2,
        borderBottomColor: 'rgba(255, 212, 0, 1)',
    },
    PlaygroundEditorTheme__markOverlapSelected: {
        backgroundColor: 'rgba(255, 212, 0, 0.7)',
        borderBottomWidth: 2,
        borderBottomColor: 'rgba(255, 212, 0, 0.7)',
    },
    PlaygroundEditorTheme__embedBlock: {
        // userSelect: 'none',
    },
    PlaygroundEditorTheme__embedBlockFocus: {
        // outlineWidth: 2,
        // outlineColor: 'rgb(60, 132, 244)',
    },
});
